<template>
  <div class="question-analysis-mobile">
    <!-- 题目难度分布 -->
    <div class="chart-card" v-show="!onlyTagCharts">
      <div class="card-header">
        <span class="card-title">题目难度分布</span>
        <div class="card-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportDifficulty" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenDifficulty" />
        </div>
      </div>
      <div class="chart-container" ref="difficultyChart"></div>
    </div>

    <!-- 题目错误分布（移动到"题目难度分布"下方） -->
    <div class="chart-card" v-show="!onlyTagCharts">
      <div class="card-header">
        <span class="card-title">题目错误分布</span>
        <div class="card-actions">
          <el-select
            v-model="errorTypeTimeRange"
            placeholder="选择时间范围"
            class="header-select time-select"
            @change="handleErrorTypeTimeRangeChange"
          >
            <el-option label="全部" value="all" />
            <el-option label="最近一个月" value="1m" />
            <el-option label="最近三个月" value="3m" />
            <el-option label="最近半年" value="6m" />
            <el-option label="最近一年" value="1y" />
          </el-select>
          <el-button type="text" icon="el-icon-download" @click="handleExportErrorType" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenErrorType" />
        </div>
      </div>
      <div class="chart-container" ref="errorTypeChart"></div>
    </div>

    <!-- 题目错误趋势（新增，位于错误分布与难度趋势之间） -->
    <question-wrong-trend-mobile v-show="!onlyTagCharts" />

    <!-- 题目难度趋势 -->
    <question-difficulty-trend-mobile v-show="!onlyTagCharts" />

    <!-- 题目掌握趋势 -->
    <question-mastery-trend-mobile v-show="!onlyTagCharts" />

    <!-- 原始分与难度关系 -->
    <div class="chart-card" v-show="!onlyTagCharts">
      <div class="card-header">
        <span class="card-title">原始分与难度关系</span>
        <div class="card-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportOriginalScoreDifficulty" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenOriginalScoreDifficulty" />
        </div>
      </div>
      <div class="chart-container" ref="originalScoreDifficultyChart"></div>
    </div>

    <!-- 订正分与难度关系 -->
    <div class="chart-card" v-show="!onlyTagCharts">
      <div class="card-header">
        <span class="card-title">订正分与难度关系</span>
        <div class="card-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportFixedScoreDifficulty" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenFixedScoreDifficulty" />
        </div>
      </div>
      <div class="chart-container" ref="fixedScoreDifficultyChart"></div>
    </div>

    <!-- 标签热度图 -->
    <div class="chart-card" v-show="showTagCharts || onlyTagCharts">
      <div class="card-header">
        <span class="card-title">标签热度图</span>
        <div class="card-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportTagCloud" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenTagCloud" />
        </div>
      </div>
      <div class="chart-container" ref="tagCloudChart"></div>
    </div>

    <!-- 标签分布图 -->
    <div class="chart-card" v-show="showTagCharts || onlyTagCharts">
      <div class="card-header">
        <span class="card-title">标签分布图</span>
        <div class="card-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportScoreTag" />
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreenScoreTag" />
        </div>
      </div>
      <div class="chart-container" ref="scoreTagChart"></div>
    </div>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="95%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
    >
      <div slot="title" class="dialog-header">
        <span>{{ dialogTitle }}</span>
        <div class="dialog-actions">
          <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          <el-button type="text" icon="el-icon-close" @click="closeDialog" />
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import 'echarts-wordcloud'
import request from '@/utils/request'
import _ from 'lodash'
import QuestionDifficultyTrendMobile from './QuestionDifficultyTrendMobile.vue'
import QuestionMasteryTrendMobile from './QuestionMasteryTrendMobile.vue'
import QuestionWrongTrendMobile from './QuestionWrongTrendMobile.vue'
import { getErrorTagInlineStyle } from '@/constants/errorTypes'

export default {
  name: 'QuestionAnalysisMobile',
  components: {
    QuestionDifficultyTrendMobile,
    QuestionMasteryTrendMobile,
    QuestionWrongTrendMobile
  },
  props: {
    showTagCharts: {
      type: Boolean,
      default: true
    },
    onlyTagCharts: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      difficultyChart: null,
      errorTypeChart: null,
      originalScoreDifficultyChart: null,
      fixedScoreDifficultyChart: null,
      tagCloudChart: null,
      scoreTagChart: null,
      chartData: {
        difficulties: [],
        errorTypeDistribution: [],
        scoreDifficulty: [],
        tags: [],
        scoreTags: [],
        tagDifficulty: [],
        questionScores: []
      },
      difficultyOrder: ['未分类', '红题', '橙题', '黄题', '绿题', '蓝题', '紫题', '黑题'],
      loading: false,
      fullscreenChart: null,
      dialogVisible: false,
      dialogTitle: '',
      currentChartType: '',
      resizeHandler: null,
      isInitializing: false,
      errorTypeTimeRange: 'all'
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts()
      this.fetchData()
    })
    window.addEventListener('resize', this.resizeCharts)
    window.addEventListener('orientationchange', this.handleOrientationChange)
  },
  beforeDestroy() {
    this.cleanupCharts()
    if (this.fullscreenChart) {
      this.fullscreenChart.dispose()
      this.fullscreenChart = null
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
      this.resizeHandler = null
    }
    window.removeEventListener('resize', this.resizeCharts)
    window.removeEventListener('orientationchange', this.handleOrientationChange)
  },
  methods: {
    dataURLToBlob(dataURL) {
      const arr = dataURL.split(',')
      const mime = arr[0].match(/:(.*?);/)[1]
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) u8arr[n] = bstr.charCodeAt(n)
      return new Blob([u8arr], { type: mime })
    },
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = filename
      link.href = url
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(url)
      document.body.removeChild(link)
    },
    initCharts() {
      try {
        this.difficultyChart = echarts.init(this.$refs.difficultyChart)
        this.errorTypeChart = echarts.init(this.$refs.errorTypeChart)
        this.originalScoreDifficultyChart = echarts.init(this.$refs.originalScoreDifficultyChart)
        this.fixedScoreDifficultyChart = echarts.init(this.$refs.fixedScoreDifficultyChart)
        this.tagCloudChart = echarts.init(this.$refs.tagCloudChart)
        this.scoreTagChart = echarts.init(this.$refs.scoreTagChart)
      } catch (error) {
        console.error('初始化图表失败:', error)
        this.$message.error('初始化图表失败')
      }
    },
    resizeCharts() {
      try {
        this.difficultyChart?.resize()
        this.errorTypeChart?.resize()
        this.originalScoreDifficultyChart?.resize()
        this.fixedScoreDifficultyChart?.resize()
        this.tagCloudChart?.resize()
        this.scoreTagChart?.resize()
      } catch (error) {
        console.error('调整图表尺寸失败:', error)
      }
    },
    handleOrientationChange() {
      // 屏幕旋转时延迟更新，确保容器尺寸已更新
      setTimeout(() => {
        try {
          this.updateScoreTagChart()
        } catch (error) {
          console.error('屏幕旋转后更新图表失败:', error)
        }
      }, 300)
    },
    cleanupCharts() {
      try {
        if (this.difficultyChart) {
          this.difficultyChart.dispose()
          this.difficultyChart = null
        }
        if (this.errorTypeChart) {
          this.errorTypeChart.dispose()
          this.errorTypeChart = null
        }

        if (this.originalScoreDifficultyChart) {
          this.originalScoreDifficultyChart.dispose()
          this.originalScoreDifficultyChart = null
        }
        if (this.fixedScoreDifficultyChart) {
          this.fixedScoreDifficultyChart.dispose()
          this.fixedScoreDifficultyChart = null
        }
        if (this.tagCloudChart) {
          this.tagCloudChart.dispose()
          this.tagCloudChart = null
        }
        if (this.scoreTagChart) {
          this.scoreTagChart.dispose()
          this.scoreTagChart = null
        }
      } catch (error) {
        console.error('清理图表失败:', error)
      }
    },
    async fetchData() {
      try {
        this.loading = true
        
        // 构建错误类型分布的请求参数
        const errorTypeParams = {}
        if (this.errorTypeTimeRange && this.errorTypeTimeRange !== 'all') {
          const end = new Date()
          const start = new Date()
          
          switch (this.errorTypeTimeRange) {
            case '1m':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              break
            case '3m':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              break
            case '6m':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 180)
              break
            case '1y':
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 365)
              break
          }
          
          errorTypeParams.start_date = start.toISOString().split('T')[0]
          errorTypeParams.end_date = end.toISOString().split('T')[0]
        }

        const [difficultyRes, errorTypeRes, scoreDifficultyRes, tagsRes, scoreTagsRes, tagDifficultyRes, questionScoresRes] = await Promise.all([
          request.get('/api/analysis/question/difficulty'),
          request.get('/api/analysis/question/error_type_distribution', { params: errorTypeParams }),
          request.get('/api/analysis/question/score_difficulty'),
          request.get('/api/analysis/question/tags'),
          request.get('/api/analysis/question/score_tags'),
          request.get('/api/analysis/question/tag_difficulty'),
          request.get('/api/analysis/question/question_scores')
        ])

        // 检查响应数据
        if (!difficultyRes.data || !difficultyRes.data.difficulties) {
          throw new Error('难度分布数据格式错误')
        }
        if (!scoreDifficultyRes.data || !scoreDifficultyRes.data.data) {
          throw new Error('得分难度数据格式错误')
        }
        if (!errorTypeRes.data || !errorTypeRes.data.data) {
          throw new Error('错误类型分布数据格式错误')
        }
        if (!tagsRes.data || !tagsRes.data.tags) {
          throw new Error('标签数据格式错误')
        }
        if (!scoreTagsRes.data || !scoreTagsRes.data.data) {
          throw new Error('得分标签数据格式错误')
        }
        if (!tagDifficultyRes.data || !tagDifficultyRes.data.data) {
          throw new Error('标签难度数据格式错误')
        }
        if (!questionScoresRes.data || !questionScoresRes.data.data) {
          throw new Error('题目得分数据格式错误')
        }

        this.chartData = {
          difficulties: difficultyRes.data.difficulties || [],
          errorTypeDistribution: errorTypeRes.data.data || [],
          scoreDifficulty: scoreDifficultyRes.data.data || [],
          tags: tagsRes.data.tags || [],
          scoreTags: scoreTagsRes.data.data || [],
          tagDifficulty: tagDifficultyRes.data.data || [],
          questionScores: questionScoresRes.data.data || []
        }

        // 详细的数据调试信息
        console.log('所有图表数据:', {
          difficulties: this.chartData.difficulties,
          errorTypeDistribution: this.chartData.errorTypeDistribution,
          scoreDifficulty: this.chartData.scoreDifficulty,
          tags: this.chartData.tags,
          scoreTags: this.chartData.scoreTags,
          tagDifficulty: this.chartData.tagDifficulty,
          questionScores: this.chartData.questionScores
        })

        this.updateCharts()
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error(error.message || '获取数据失败')
      } finally {
        this.loading = false
      }
    },
    getRandomColor() {
      const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272']
      return colors[Math.floor(Math.random() * colors.length)]
    },
    updateCharts() {
      try {
        this.updateDifficultyChart()
        this.updateErrorTypeChart()
        this.updateOriginalScoreDifficultyChart()
        this.updateFixedScoreDifficultyChart()
        this.updateTagCloudChart()
        this.updateScoreTagChart()
      } catch (error) {
        console.error('更新图表失败:', error)
        this.$message.error('更新图表失败')
      }
    },
    updateErrorTypeChart() {
      try {
        const errorData = this.chartData.errorTypeDistribution
        const pieData = []
        errorData.forEach(item => {
          const name = item.error_name || '未定义'
          const total = item.total || 0
          if (total > 0) {
            const color = item.error_code ? (getErrorTagInlineStyle(item.error_code)?.backgroundColor || '#1E90FF') : '#E0E0E0'
            pieData.push({ name, value: total, itemStyle: { color, borderColor: '#fff', borderWidth: 2 } })
          }
        })

        const option = {
          backgroundColor: '#ffffff',
          tooltip: {
            trigger: 'item',
            confine: true,
            enterable: false,
            hideDelay: 100,
            backgroundColor: 'rgba(50, 50, 50, 0.9)',
            borderColor: '#333',
            textStyle: {
              color: '#fff',
              fontSize: 11
            },
            formatter: (params) => {
              const name = params.name || '未定义'
              const item = errorData.find(e => (e.error_name || '未定义') === name) || {}
              const total = item.total || 0
              const fixed = item.fixed || 0
              const unfixed = Math.max(0, total - fixed)
              const rate = total > 0 ? (fixed / total * 100).toFixed(2) : '0.00'
              
              // 计算该错误类型占总数的比例
              const totalAll = errorData.reduce((sum, e) => sum + (e.total || 0), 0)
              const totalRate = totalAll > 0 ? (total / totalAll * 100).toFixed(2) : '0.00'
              
              return `
                <div style="padding: 4px;">
                  <div style="font-weight: bold; margin-bottom: 6px;">${name}</div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                    <div>总数：${total}（${totalRate}%）</div>
                    <div>已订正：${fixed}</div>
                    <div>未订正：${unfixed}</div>
                    <div>订正率：${rate}%</div>
                  </div>
                </div>
              `
            }
          },
          legend: {
            type: 'plain',
            orient: 'horizontal',
            bottom: '4%',
            left: 'center',
            itemGap: 8,
            itemWidth: 12,
            itemHeight: 6,
            textStyle: { fontSize: 10 },
            data: errorData.map(i => i.error_name || '未定义'),
            selectedMode: true,  // 启用图例点击
            width: '95%',
            padding: [0, 0, 0, 0],
            align: 'auto'
          },
          series: [
            {
              type: 'pie',
              radius: ['45%', '65%'],
              center: ['50%', '40%'],
              label: { show: false },
              data: pieData,
              itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 }
            }
          ]
        }
        this.errorTypeChart.setOption(option, true)
      } catch (error) {
        console.error('更新错误类型分布图失败:', error)
      }
    },
    updateDifficultyChart() {
      try {
        // 题目难度分布图
        const sortedDifficulties = this.difficultyOrder.map(difficulty => {
          return this.chartData.difficulties.find(item => item.name === difficulty) || {
            name: difficulty,
            value: 0
          }
        })

        const option = {
          backgroundColor: '#ffffff',
          tooltip: {
            trigger: 'item',
            confine: true,
            enterable: false,
            hideDelay: 100,
            backgroundColor: 'rgba(50, 50, 50, 0.9)',
            borderColor: '#333',
            textStyle: {
              color: '#fff',
              fontSize: 11
            },
            formatter: function(params) {
              if (!params || !params.data) return ''
              const name = params.name || ''
              const value = params.value || 0
              const percent = params.percent || 0
              
              // 获取难度标签样式
              const getDifficultyTagStyle = (difficulty) => {
                const index = this.difficultyOrder.indexOf(difficulty)
                if (index === -1) return null
                const colors = [
                  '#D9D9D9',  // 未分类：灰色
                  '#FFA39E',  // 红题：红色
                  '#FFC069',  // 橙题：橙色
                  '#FFF566',  // 黄题：黄色
                  '#95DE64',  // 绿题：绿色
                  '#69C0FF',  // 蓝题：蓝色
                  '#B37FEB',  // 紫题：紫色
                  '#595959'   // 黑题：黑色
                ]
                const color = colors[index] || colors[0]
                // 将颜色转换为 rgba 格式用于背景
                const hexToRgba = (hex, alpha) => {
                  const r = parseInt(hex.slice(1, 3), 16)
                  const g = parseInt(hex.slice(3, 5), 16)
                  const b = parseInt(hex.slice(5, 7), 16)
                  return `rgba(${r}, ${g}, ${b}, ${alpha})`
                }
                const bg = hexToRgba(color, 0.2)
                return `display: inline-block; padding: 0px 4px; margin-right: 4px; border-radius: 2px; font-size: 10px; background-color: ${bg}; border: 1px solid ${color}; color: ${color}; line-height: 1.2;`
              }
              
              const tagStyle = getDifficultyTagStyle(name)
              const titleHtml = tagStyle 
                ? `<span style="${tagStyle}">${name}</span>`
                : name
              
              return `
                <div style="padding: 4px;">
                  <div style="font-weight: bold; margin-bottom: 6px;">${titleHtml}</div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                    <div>数量：${value}</div>
                    <div>占比：${percent.toFixed(1)}%</div>
                  </div>
                </div>
              `
            }.bind(this)
          },
          legend: {
            type: 'plain',
            orient: 'horizontal',
            bottom: '8%',
            left: 'center',
            itemGap: 12,
            itemWidth: 20,
            itemHeight: 10,
            textStyle: {
              fontSize: 12
            },
            data: this.difficultyOrder,
            width: '90%',
            padding: [0, 0, 0, 0],
            align: 'auto',
            selectedMode: true,  // 启用图例点击
            pageIcons: false,
            inactiveColor: '#ccc',
            pageTextStyle: {
              color: 'transparent'
            },
            pageButtonItemGap: 0,
            pageButtonGap: 0,
            pageButtonPosition: 'end',
            pageFormatter: '{current}/{total}',
            pageIconColor: 'transparent',
            pageIconInactiveColor: 'transparent',
            pageIconSize: 0,
            itemStyle: {
              borderWidth: 0
            }
          },
          color: [
            '#D9D9D9',  // 未分类：灰色
            '#FFA39E',  // 红题：红色
            '#FFC069',  // 橙题：橙色
            '#FFF566',  // 黄题：黄色
            '#95DE64',  // 绿题：绿色
            '#69C0FF',  // 蓝题：蓝色
            '#B37FEB',  // 紫题：紫色
            '#595959'   // 黑题：黑色
          ],
          series: [{
            name: '题目难度',
            type: 'pie',
            radius: ['45%', '65%'],
            center: ['50%', '40%'],
            avoidLabelOverlap: true,
            itemStyle: {
              borderRadius: 6,
              borderColor: '#fff',
              borderWidth: 2
            },
            label: {
              show: false
            },
            emphasis: {
              label: {
                show: true,
                fontSize: '16',
                fontWeight: 'bold'
              }
            },
            labelLine: {
              show: false
            },
            data: sortedDifficulties
          }]
        }

        this.difficultyChart.setOption(option, true)
      } catch (error) {
        console.error('更新图表失败:', error)
      }
    },


    updateOriginalScoreDifficultyChart() {
      try {
        // 原始分与难度关系图
        const option = {
          backgroundColor: '#ffffff',
          tooltip: {
            trigger: 'item',
            confine: true,
            enterable: false,
            hideDelay: 100,
            backgroundColor: 'rgba(50, 50, 50, 0.9)',
            borderColor: '#333',
            textStyle: {
              color: '#fff',
              fontSize: 11
            },
            formatter: function(params) {
              if (!params.data || params.data.length < 3) return '';
              const origIndex = params.data[2];
              if (origIndex < 0 || origIndex >= this.chartData.scoreDifficulty.length) return '';
              const rawData = this.chartData.scoreDifficulty[origIndex];
              if (!rawData || rawData.original_count === 0) return '';
              const difficulty = rawData.difficulty || '未分类'
              return `
                <div style="padding: 4px;">
                  <div style="font-weight: bold; margin-bottom: 6px;">${difficulty}</div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                    <div>分数区间：${rawData.score_range}</div>
                    <div>原始分题目数量：${rawData.original_count}</div>
                  </div>
                </div>
              `
            }.bind(this),
            position: 'top'
          },
          grid: {
            left: '5%',
            right: '5%',
            bottom: '10%',
            top: '20%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: this.difficultyOrder,
            boundaryGap: true,
            axisTick: {
              alignWithLabel: true
            },
            axisLabel: {
              fontSize: 10,
              rotate: 45
            }
          },
          yAxis: {
            type: 'value',
            name: '分数',
            min: 0,
            max: 100,
            interval: 20,
            nameTextStyle: {
              fontSize: 10,
              color: '#666'
            },
            axisLabel: {
              fontSize: 10,
              formatter: function(value) {
                if (value === 100) return '100分';
                if (value >= 76) return '76-99分';
                if (value >= 51) return '51-75分';
                if (value >= 26) return '26-50分';
                if (value >= 1) return '1-25分';
                return '0分';
              }
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed'
              }
            }
          },
          series: [
            {
              type: 'scatter',
              symbolSize: function (data) {
                const item = this.chartData.scoreDifficulty[data[2]];
                if (!item || item.original_count === 0) return 0;
                return Math.max(5, Math.min(60, Math.sqrt(item.original_count) * 6));
              }.bind(this),
              itemStyle: {
                color: '#409EFF',
                opacity: 0.7
              },
              data: this.chartData.scoreDifficulty.filter(item => item.original_count > 0).map((item, index) => [
                this.difficultyOrder.indexOf(item.difficulty || '未分类'),
                item.y_value,
                this.chartData.scoreDifficulty.indexOf(item)
              ])
            }
          ]
        }
        this.originalScoreDifficultyChart.setOption(option, true)
      } catch (error) {
        console.error('更新图表失败:', error)
      }
    },
    updateFixedScoreDifficultyChart() {
      try {
        // 订正分与难度关系图
        const option = {
          backgroundColor: '#ffffff',
          tooltip: {
            trigger: 'item',
            confine: true,
            enterable: false,
            hideDelay: 100,
            backgroundColor: 'rgba(50, 50, 50, 0.9)',
            borderColor: '#333',
            textStyle: {
              color: '#fff',
              fontSize: 11
            },
            formatter: function(params) {
              if (!params.data || params.data.length < 3) return '';
              const origIndex = params.data[2];
              if (origIndex < 0 || origIndex >= this.chartData.scoreDifficulty.length) return '';
              const rawData = this.chartData.scoreDifficulty[origIndex];
              if (!rawData || rawData.fixed_count === 0) return '';
              const difficulty = rawData.difficulty || '未分类'
              return `
                <div style="padding: 4px;">
                  <div style="font-weight: bold; margin-bottom: 6px;">${difficulty}</div>
                  <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                    <div>分数区间：${rawData.score_range}</div>
                    <div>订正分题目数量：${rawData.fixed_count}</div>
                  </div>
                </div>
              `
            }.bind(this),
            position: 'top'
          },
          grid: {
            left: '5%',
            right: '5%',
            bottom: '10%',
            top: '20%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: this.difficultyOrder,
            boundaryGap: true,
            axisTick: {
              alignWithLabel: true
            },
            axisLabel: {
              fontSize: 10,
              rotate: 45
            }
          },
          yAxis: {
            type: 'value',
            name: '分数',
            min: 0,
            max: 100,
            interval: 20,
            nameTextStyle: {
              fontSize: 10,
              color: '#666'
            },
            axisLabel: {
              fontSize: 10,
              formatter: function(value) {
                if (value === 100) return '100分';
                if (value >= 76) return '76-99分';
                if (value >= 51) return '51-75分';
                if (value >= 26) return '26-50分';
                if (value >= 1) return '1-25分';
                return '0分';
              }
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed'
              }
            }
          },
          series: [
            {
              type: 'scatter',
              symbolSize: function (data) {
                const item = this.chartData.scoreDifficulty[data[2]];
                if (!item || item.fixed_count === 0) return 0;
                return Math.max(5, Math.min(60, Math.sqrt(item.fixed_count) * 6));
              }.bind(this),
              itemStyle: {
                color: '#67C23A',
                opacity: 0.7
              },
              data: this.chartData.scoreDifficulty.filter(item => item.fixed_count > 0).map((item, index) => [
                this.difficultyOrder.indexOf(item.difficulty || '未分类'),
                item.y_value,
                this.chartData.scoreDifficulty.indexOf(item)
              ])
            }
          ]
        }
        this.fixedScoreDifficultyChart.setOption(option, true)
      } catch (error) {
        console.error('更新图表失败:', error)
      }
    },
    updateTagCloudChart() {
      try {
        // 标签词云图
        const option = {
          backgroundColor: '#ffffff',
          tooltip: {
            trigger: 'item',
            confine: true,
            enterable: false,
            hideDelay: 100,
            backgroundColor: 'rgba(50, 50, 50, 0.9)',
            borderColor: '#333',
            textStyle: {
              color: '#fff',
              fontSize: 11
            }
          },
          series: [{
            type: 'wordCloud',
            shape: 'circle',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            sizeRange: [10, 28],
            rotationRange: [-90, 90],
            rotationStep: 45,
            gridSize: 6,
            drawOutOfBound: false,
            textStyle: {
              fontFamily: 'sans-serif',
              fontWeight: 'bold',
              color: function () {
                return 'rgb(' + [
                  Math.round(Math.random() * 160),
                  Math.round(Math.random() * 160),
                  Math.round(Math.random() * 160)
                ].join(',') + ')'
              }
            },
            emphasis: {
              focus: 'self',
              textStyle: {
                shadowBlur: 10,
                shadowColor: '#333'
              }
            },
            data: this.chartData.tags.map(tag => ({
              name: tag.name,
              value: tag.value,
              textStyle: {
                color: this.getRandomColor()
              }
            }))
          }]
        }
        this.tagCloudChart.setOption(option, true)
      } catch (error) {
        console.error('更新图表失败:', error)
      }
    },
    updateScoreTagChart() {
      try {
        // 标签分布图
        const sortedTags = [...new Set([
          ...this.chartData.scoreTags.map(item => item.tag),
          ...this.chartData.tagDifficulty.map(item => item.tag)
        ])].sort()
        const option = {
          backgroundColor: '#ffffff',
          tooltip: {
            trigger: 'item',
            confine: true,
            enterable: false,
            hideDelay: 100,
            backgroundColor: 'rgba(50, 50, 50, 0.9)',
            borderColor: '#333',
            textStyle: {
              color: '#fff',
              fontSize: 11
            },
            formatter: function(params) {
              const data = params.data
              if (!data) return ''
              const tag = data.tag || ''
              if (params.seriesName === '平均分') {
                return `
                  <div style="padding: 4px;">
                    <div style="font-weight: bold; margin-bottom: 6px;">${tag}</div>
                    <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                      <div>平均分：${data.score.toFixed(1)}</div>
                      <div>题目数：${data.count}</div>
                    </div>
                  </div>
                `
              } else if (params.seriesName === '难度') {
                let difficultyName = '';
                if (data.difficulty === 0) {
                  difficultyName = '未分类';
                } else if (data.difficulty === 100) {
                  difficultyName = '黑题';
                } else {
                  const step = 100 / 7;
                  const index = Math.floor(data.difficulty / step);
                  difficultyName = this.difficultyOrder[index] || '未分类';
                }
                return `
                  <div style="padding: 4px;">
                    <div style="font-weight: bold; margin-bottom: 6px;">${tag}</div>
                    <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                      <div>平均难度：${difficultyName}</div>
                      <div>题目数：${data.count}</div>
                    </div>
                  </div>
                `
              } else {
                return `
                  <div style="padding: 4px;">
                    <div style="font-weight: bold; margin-bottom: 6px;">${tag}</div>
                    <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                      <div>题目名称：${data.question_name || ''}</div>
                      <div>得分：${data.score.toFixed(1)}</div>
                    </div>
                  </div>
                `
              }
            }.bind(this)
          },
          legend: {
            data: ['得分', '平均分', '难度'],
            bottom: 0,
            left: 'center',
            itemGap: 8,
            itemWidth: 10,
            itemHeight: 6,
            textStyle: {
              fontSize: 10,
              color: '#666'
            }
          },
          grid: {
            left: '5%',
            right: '5%',
            bottom: '15%',
            top: '15%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: sortedTags,
            axisLabel: {
              rotate: 45,
              interval: (index, value) => {
                const total = sortedTags.length;
                if (total <= 4) return true;
                const containerWidth = this.$refs.scoreTagChart ? this.$refs.scoreTagChart.offsetWidth : 300;
                let maxLabels;
                if (containerWidth >= 600) {
                  maxLabels = Math.min(total, 24);
                } else if (containerWidth >= 400) {
                  maxLabels = Math.min(total, 12);
                } else {
                  maxLabels = Math.min(total, 8);
                }
                if (index === 0 || index === total - 1) return true;
                const step = Math.max(1, Math.floor(total / maxLabels));
                return index % step === 0;
              },
              fontSize: 10,
              align: 'right',
              verticalAlign: 'middle',
              margin: 0,
              overflow: 'truncate',
              width: 60,
              height: 20
            },
            axisTick: {
              show: false
            }
          },
          yAxis: [
            {
              type: 'value',
              name: '平均分/得分',
              min: 0,
              max: 100,
              interval: 20,
              minInterval: 20,
              splitNumber: 5,
              nameTextStyle: {
                fontSize: 10,
                color: '#666'
              },
              axisLabel: {
                fontSize: 10
              },
              splitLine: {
                show: true,
                lineStyle: {
                  type: 'dashed'
                }
              }
            },
            {
              type: 'value',
              name: '难度',
              min: 0,
              max: 100,
              interval: 14.29,
              position: 'right',
              nameTextStyle: {
                fontSize: 10,
                color: '#666'
              },
              axisLabel: {
                fontSize: 10,
                formatter: (value) => {
                  if (value === 0) return '未分类';
                  if (value === 100) return '黑题';
                  const step = 100 / 7;
                  const index = Math.floor(value / step);
                  if (index >= 0 && index < this.difficultyOrder.length - 1) {
                    return this.difficultyOrder[index];
                  }
                  return '';
                }
              },
              splitLine: {
                show: false
              },
              axisLine: {
                show: false
              }
            }
          ],
          series: [
            {
              name: '得分',
              type: 'scatter',
              yAxisIndex: 0,
              data: this.chartData.questionScores
                .filter(item => sortedTags.indexOf(item.tag) !== -1)
                .map(item => ({
                  value: [
                    sortedTags.indexOf(item.tag),
                    item.score
                  ],
                  tag: item.tag,
                  question_name: item.question_name,
                  score: item.score,
                  symbolSize: 8
                })),
              itemStyle: {
                color: '#B37FEB',
                opacity: 0.7
              }
            },
            {
              name: '平均分',
              type: 'scatter',
              yAxisIndex: 0,
              data: this.chartData.scoreTags
                .filter(item => sortedTags.indexOf(item.tag) !== -1)
                .map(item => ({
                  value: [
                    sortedTags.indexOf(item.tag),
                    item.score
                  ],
                  tag: item.tag,
                  score: item.score,
                  count: item.count,
                  symbolSize: Math.max(8, Math.min(40, Math.sqrt(item.count) * 8))
                })),
              itemStyle: {
                color: '#409EFF',
                opacity: 0.8
              }
            },
            {
              name: '难度',
              type: 'scatter',
              yAxisIndex: 1,
              data: this.chartData.tagDifficulty
                .filter(item => sortedTags.indexOf(item.tag) !== -1)
                .map(item => {
                  const difficultyIndex = this.difficultyOrder.indexOf(item.difficulty || '未分类');
                  let avgDifficulty = 0;
                  if (difficultyIndex === 0) {
                    avgDifficulty = 0;
                  } else if (difficultyIndex === 7) {
                    avgDifficulty = 100;
                  } else {
                    const step = 100 / 7;
                    avgDifficulty = difficultyIndex * step;
                  }
                  return {
                    value: [
                      sortedTags.indexOf(item.tag),
                      avgDifficulty
                    ],
                    tag: item.tag,
                    difficulty: avgDifficulty,
                    count: item.count,
                    symbolSize: Math.max(8, Math.min(40, Math.sqrt(item.count) * 8))
                  }
                }),
              itemStyle: {
                color: '#67C23A',
                opacity: 0.8
              }
            }
          ]
        }
        this.scoreTagChart.setOption(option, true)
      } catch (error) {
        console.error('更新图表失败:', error)
      }
    },
    // 导出功能
    handleExportDifficulty() {
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.difficultyChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'difficulty_distribution.png')
      } catch (error) {
        console.error('导出题目难度分布图失败:', error)
        this.$message.error('导出图片失败')
      }
    },

    handleExportErrorType() {
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.errorTypeChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'error_type_distribution.png')
      } catch (error) {
        console.error('导出错误类型分布图失败:', error)
        this.$message.error('导出图片失败')
      }
    },


    handleExportOriginalScoreDifficulty() {
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.originalScoreDifficultyChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'score_vs_difficulty_original.png')
      } catch (error) {
        console.error('导出原始分与难度关系图失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleExportFixedScoreDifficulty() {
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.fixedScoreDifficultyChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'score_vs_difficulty_fixed.png')
      } catch (error) {
        console.error('导出订正分与难度关系图失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleExportTagCloud() {
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.tagCloudChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'tag_cloud.png')
      } catch (error) {
        console.error('导出标签热度图失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    handleExportScoreTag() {
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.scoreTagChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, 'tag_distribution.png')
      } catch (error) {
        console.error('导出标签分布图失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    // 全屏功能
    handleFullscreenDifficulty() {
      this.dialogTitle = '题目难度分布'
      this.currentChartType = 'difficulty'
      this.dialogVisible = true
    },
    handleFullscreenErrorType() {
      this.dialogTitle = '题目错误分布'
      this.currentChartType = 'errorType'
      this.dialogVisible = true
    },

    handleFullscreenOriginalScoreDifficulty() {
      this.dialogTitle = '原始分与难度关系'
      this.currentChartType = 'originalScoreDifficulty'
      this.dialogVisible = true
    },
    handleFullscreenFixedScoreDifficulty() {
      this.dialogTitle = '订正分与难度关系'
      this.currentChartType = 'fixedScoreDifficulty'
      this.dialogVisible = true
    },
    handleFullscreenTagCloud() {
      this.dialogTitle = '标签热度图'
      this.currentChartType = 'tagCloud'
      this.dialogVisible = true
    },
    handleFullscreenScoreTag() {
      this.dialogTitle = '标签分布图'
      this.currentChartType = 'scoreTag'
      this.dialogVisible = true
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom) return

          this.fullscreenChart = echarts.init(chartDom)
          let option = null

          // 根据当前图表类型获取配置
          switch (this.currentChartType) {
            case 'difficulty':
              option = this.difficultyChart.getOption()
              break
            case 'errorType':
              option = this.errorTypeChart.getOption()
              if (option && option.series && option.series[0]) {
                option.series[0].radius = ['35%', '55%']
                option.series[0].center = ['50%', '50%']
              }
              break
            case 'originalScoreDifficulty':
              option = this.originalScoreDifficultyChart.getOption()
              break
            case 'fixedScoreDifficulty':
              option = this.fixedScoreDifficultyChart.getOption()
              break
            case 'tagCloud':
              option = this.tagCloudChart.getOption()
              break
            case 'scoreTag':
              option = this.scoreTagChart.getOption()
              break
          }

          if (option) {
            // 全屏时调整配置
            if (option.grid) {
              option.grid = {
                left: '5%',
                right: '5%',
                bottom: '10%',
                top: '10%',
                containLabel: true
              }
            }

            // 调整tooltip配置以适应全屏
            if (option.tooltip) {
              option.tooltip.confine = true
              option.tooltip.enterable = false
              option.tooltip.hideDelay = 100
            }

            this.fullscreenChart.setOption(option)
            
            this.resizeHandler = _.debounce(() => {
              if (this.fullscreenChart) {
                this.fullscreenChart.resize()
              }
            }, 100)

            window.addEventListener('resize', this.resizeHandler)
          }
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClosed() {
      this.isInitializing = false
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    closeDialog() {
      this.dialogVisible = false
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      
      try {
        const dpr = window.devicePixelRatio || 1
        const pixelRatio = Math.min(3, dpr * 1.5)
        const dataURL = this.fullscreenChart.getDataURL({ type: 'png', pixelRatio, backgroundColor: '#fff' })
        const blob = this.dataURLToBlob(dataURL)
        this.downloadBlob(blob, `${this.dialogTitle}.png`)
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    // 处理错误类型时间范围变化
    handleErrorTypeTimeRangeChange() {
      console.log('错误类型时间范围变化:', this.errorTypeTimeRange)
      this.fetchData()
    }
  }
}
</script>

<style scoped>
.question-analysis-mobile {
  width: 100%;
  padding: 0;
  box-sizing: border-box;
}

.chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
}

.card-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
}

.card-actions .el-button {
  color: #ffffff;
  font-size: 16px;
  padding: 4px;
  margin-left: 8px;
}

.card-actions .el-button:hover {
  color: #409EFF;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.header-select {
  width: 80px;
  margin-right: 8px;
}

.header-select :deep(.el-input__inner) {
  height: 32px;
  line-height: 32px;
  border-radius: 4px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 12px;
}

.header-select :deep(.el-input__inner)::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.header-select :deep(.el-input__suffix) {
  color: #ffffff;
}

.header-select.time-select {
  width: 80px;
  margin-right: 8px;
}

.chart-container {
  width: 100%;
  height: 300px;
  padding: 0;
  box-sizing: border-box;
}

/* 移动端优化 */
@media (max-width: 480px) {
  .question-analysis-mobile {
    padding: 0;
  }
  
  .chart-card {
    margin-bottom: 12px;
  }
  
  .chart-container {
    height: 300px;
  }
  
  .card-header {
    padding: 12px 16px;
  }
  
  .card-title {
    font-size: 14px;
  }
  
  .card-actions .el-button {
    font-size: 14px;
    padding: 2px;
    margin-left: 4px;
  }
  
  .header-select {
    width: 60px;
    margin-right: 6px;
  }
  
  .header-select :deep(.el-input__inner) {
    height: 28px;
    line-height: 28px;
    font-size: 11px;
  }
  
  .dialog-header {
    font-size: 14px;
  }
  
  .dialog-actions .el-button {
    font-size: 14px;
    padding: 2px;
    margin-left: 4px;
  }
  
  .fullscreen-chart {
    height: 400px;
    padding: 0;
  }
}

/* 全屏弹窗样式 */
.chart-dialog {
  margin: 0;
}

.chart-dialog .el-dialog {
  margin: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #2E365A;
}

.chart-dialog :deep(.el-dialog__header) {
  background-color: #2E365A;
  padding: 0;
  margin: 0;
  border-bottom: none;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  flex: 1;
  padding: 0;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-actions {
  display: flex;
  gap: 8px;
}

.dialog-actions .el-button {
  color: #fff;
  padding: 4px;
}

.dialog-actions .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 500px;
  padding: 0;
  box-sizing: border-box;
}
</style> 